import { Manager } from '../../../../../core';
import { logger } from '../../../../../core';

function parseCommaSeparatedValues(rawValue) {
  if (typeof rawValue !== 'string') {
    return [];
  }

  return rawValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function buildInFilter(rawValue) {
  const values = parseCommaSeparatedValues(rawValue);
  if (values.length === 0) {
    return undefined;
  }

  if (values.length === 1) {
    return { eq: values[0] };
  }

  return { in: values };
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function parseRecordDate(value) {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfUtcDay(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function startOfUtcHour(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours())
  );
}

function startOfUtcMinute(date) {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes()
    )
  );
}

function addUtcDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function addUtcHours(date, hours) {
  const next = new Date(date);
  next.setUTCHours(next.getUTCHours() + hours);
  return next;
}

function addUtcMinutes(date, minutes) {
  const next = new Date(date);
  next.setUTCMinutes(next.getUTCMinutes() + minutes);
  return next;
}

function addUtcMonths(date, months) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

function startOfUtcWeek(date) {
  const next = startOfUtcDay(date);
  next.setUTCDate(next.getUTCDate() - next.getUTCDay());
  return next;
}

function formatDayKey(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatMonthKey(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
}

function formatHourKey(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(date.getUTCHours()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}-${hh}`;
}

function formatMinuteKey(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}-${hh}:${min}`;
}

function formatBucketLabel(date, granularity) {
  if (granularity === 'month') {
    return date.toLocaleString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  }

  if (granularity === 'week') {
    const weekEnd = addUtcDays(date, 6);
    const startLabel = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    const endLabel = weekEnd.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    return `${startLabel} - ${endLabel}`;
  }

  if (granularity === 'hour') {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
    });
  }

  if (granularity === 'minute') {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

const INFERENCE_SERIES_COLORS = [
  '#8b0000',
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#a855f7',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

const INFERENCE_VISUALIZATION_GRANULARITIES = ['minute', 'hour', 'day', 'week', 'month'];
const INFERENCE_CHART_POINT_LIMITS = {
  plot: 2000,
  bar: 2000,
};

export class AdminInferencesManager extends Manager {
  constructor(initialQuery = {}) {
    super();
    this.initialQuery = initialQuery || {};
    this.queryState = this.initialQuery || {};
    this.records = [];
    this.meta = {
      total: 0,
      count: 0,
      limit: 1000,
      offset: 0,
    };
    this.range = null;
    this.rangeField = 'startedAt';
    this.lastError = null;
    this.isLoading = false;
  }

  async initGlobalVariables() {
    this.queryState = this.initialQuery || {};
    this.records = [];
    this.meta = {
      total: 0,
      count: 0,
      limit: Number(this.queryState?.limit || 1000) || 1000,
      offset: 0,
    };
    this.range = null;
    this.rangeField = 'startedAt';
    this.lastError = null;
    this.isLoading = false;
  }

  async initSystems() {
    await this.loadInferenceRecords(this.queryState);
    this.isInitialized = true;

    logger.log(
      { tags: 'admin-inferences-manager', color1: 'blue' },
      'AdminInferencesManager initialized successfully'
    );
  }

  _buildQueryPayload(queryState = {}) {
    const nextQuery = queryState || {};
    const filters = {};

    const modeFilter = buildInFilter(nextQuery.mode);
    if (modeFilter && String(nextQuery.mode).toLowerCase() !== 'all') {
      filters.mode = modeFilter;
    }

    const instanceFilter = buildInFilter(nextQuery.instance);
    if (instanceFilter) {
      filters.instance = instanceFilter;
    }

    const phaseFilter = buildInFilter(nextQuery.phase);
    if (phaseFilter) {
      filters.phase = phaseFilter;
    }

    const statusFilter = buildInFilter(nextQuery.status);
    if (statusFilter) {
      filters.status = statusFilter;
    }

    const modelIdFilter = buildInFilter(nextQuery.modelId);
    if (modelIdFilter) {
      filters.modelId = modelIdFilter;
    }

    const providerFilter = buildInFilter(nextQuery.provider);
    if (providerFilter) {
      filters.provider = providerFilter;
    }

    const userIdFilter = buildInFilter(nextQuery.userId);
    if (userIdFilter) {
      filters.userId = userIdFilter;
    }

    const appIdFilter = buildInFilter(nextQuery.appId);
    const membershipIdFilter = buildInFilter(nextQuery.membershipId);
    if (appIdFilter || membershipIdFilter) {
      filters.instanceContext = {};
      if (appIdFilter) {
        filters.instanceContext.appId = appIdFilter;
      }
      if (membershipIdFilter) {
        filters.instanceContext.membershipId = membershipIdFilter;
      }
    }

    return {
      range: {
        field: 'startedAt',
        preset: nextQuery.rangePreset || 'last_month',
        customStart: nextQuery.customStart || null,
        customEnd: nextQuery.customEnd || null,
      },
      filters,
      sort: {
        field: 'startedAt',
        dir: 'desc',
      },
      pagination: {
        limit: Number(nextQuery.limit || 1000) || 1000,
        offset: Number(nextQuery.offset || 0) || 0,
      },
    };
  }

  async loadInferenceRecords(queryState = {}) {
    this.isLoading = true;
    this.lastError = null;
    this.queryState = queryState || {};

    try {
      const response = await fetch('/api/admin/inferences/query', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(this._buildQueryPayload(this.queryState)),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to query inference records');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Invalid inference query response');
      }

      this.records = Array.isArray(data.records) ? data.records : [];
      this.meta = {
        total: Number(data?.meta?.total || 0) || 0,
        count: Number(data?.meta?.count || 0) || 0,
        limit: Number(data?.meta?.limit || this.queryState?.limit || 1000) || 1000,
        offset: Number(data?.meta?.offset || 0) || 0,
      };
      this.range = data?.range || null;
      this.rangeField = data?.rangeField || 'startedAt';
    } catch (error) {
      this.records = [];
      this.meta = {
        total: 0,
        count: 0,
        limit: Number(this.queryState?.limit || 1000) || 1000,
        offset: Number(this.queryState?.offset || 0) || 0,
      };
      this.range = null;
      this.rangeField = 'startedAt';
      this.lastError = error?.message || 'Failed to query inference records.';

      logger.error(
        { tags: 'admin-inferences-manager|error', color1: 'red' },
        'Failed to load inference records:',
        error
      );
    } finally {
      this.isLoading = false;
    }
  }

  async refreshRecords(queryState = null) {
    const nextQuery = queryState || this.queryState || {};
    await this.loadInferenceRecords(nextQuery);
  }

  getRecords() {
    return this.records;
  }

  getMeta() {
    return this.meta;
  }

  getRange() {
    return this.range;
  }

  getRangeField() {
    return this.rangeField;
  }

  getLastError() {
    return this.lastError;
  }

  getActiveFilterCount(queryState = null) {
    const nextQuery = queryState || this.queryState || {};
    const candidates = [
      nextQuery.mode && String(nextQuery.mode).toLowerCase() !== 'all' ? nextQuery.mode : '',
      nextQuery.instance,
      nextQuery.phase,
      nextQuery.status,
      nextQuery.modelId,
      nextQuery.provider,
      nextQuery.userId,
      nextQuery.appId,
      nextQuery.membershipId,
    ];

    return candidates.filter((value) => typeof value === 'string' && value.trim().length > 0).length;
  }

  getSummary(queryState = null) {
    const nextQuery = queryState || this.queryState || {};
    return {
      totalMatchingRecords: this.meta.total || 0,
      loadedRecords: this.meta.count || 0,
      queryLimit: this.meta.limit || Number(nextQuery.limit || 1000) || 1000,
      activeFilterCount: this.getActiveFilterCount(nextQuery),
      rangeField: this.rangeField || 'startedAt',
    };
  }

  getMetricOptions() {
    return [
      { value: 'records', label: 'Record Count' },
      { value: 'total_tokens', label: 'Total Tokens' },
      { value: 'cost', label: 'Total Cost' },
    ];
  }

  getMetricLabel(metric) {
    if (metric === 'total_tokens') {
      return 'Total Tokens';
    }
    if (metric === 'cost') {
      return 'Total Cost';
    }
    return 'Record Count';
  }

  getBreakdownFieldOptions() {
    return [
      { value: 'none', label: 'No Breakdown' },
      { value: 'modelId', label: 'Model ID' },
      { value: 'instance', label: 'Instance' },
    ];
  }

  getBreakdownValues(field = 'none') {
    if (field === 'none') {
      return [];
    }

    const values = new Set();
    for (const record of this.records || []) {
      const value = this._getBreakdownValue(record, field);
      if (value) {
        values.add(value);
      }
    }

    return Array.from(values).sort((a, b) => String(a).localeCompare(String(b)));
  }

  getVisualizationOptions(field = 'none') {
    return {
      metricOptions: this.getMetricOptions(),
      breakdownFieldOptions: this.getBreakdownFieldOptions(),
      breakdownValueOptions: this.getBreakdownValues(field).map((value) => ({
        value,
        label: value,
      })),
    };
  }

  getSeriesColor(key) {
    if (!key) {
      return INFERENCE_SERIES_COLORS[0];
    }

    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
      hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
    }

    return INFERENCE_SERIES_COLORS[hash % INFERENCE_SERIES_COLORS.length];
  }

  _getMetricValue(record, metric = 'records') {
    if (metric === 'total_tokens') {
      return Number(record?.usage?.total_tokens || 0) || 0;
    }

    if (metric === 'cost') {
      return Number(record?.usage?.cost || 0) || 0;
    }

    return 1;
  }

  _getBreakdownValue(record, breakdownField = 'none') {
    if (breakdownField === 'modelId') {
      return record?.modelId || 'unknown';
    }

    if (breakdownField === 'instance') {
      return record?.instance || 'unknown';
    }

    return null;
  }

  _getBucketStart(date, granularity = 'day') {
    if (granularity === 'month') {
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    }

    if (granularity === 'week') {
      return startOfUtcWeek(date);
    }

    if (granularity === 'hour') {
      return startOfUtcHour(date);
    }

    if (granularity === 'minute') {
      return startOfUtcMinute(date);
    }

    return startOfUtcDay(date);
  }

  _getBucketKey(date, granularity = 'day') {
    const bucketStart = this._getBucketStart(date, granularity);
    if (granularity === 'month') {
      return formatMonthKey(bucketStart);
    }

    if (granularity === 'hour') {
      return formatHourKey(bucketStart);
    }

    if (granularity === 'minute') {
      return formatMinuteKey(bucketStart);
    }

    return formatDayKey(bucketStart);
  }

  _shiftBucketStart(date, granularity = 'day', delta = 0) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime()) || !delta) {
      return date instanceof Date ? new Date(date) : null;
    }

    if (granularity === 'minute') {
      return addUtcMinutes(date, delta);
    }

    if (granularity === 'hour') {
      return addUtcHours(date, delta);
    }

    if (granularity === 'day') {
      return addUtcDays(date, delta);
    }

    if (granularity === 'week') {
      return addUtcDays(date, delta * 7);
    }

    if (granularity === 'month') {
      return addUtcMonths(date, delta);
    }

    return addUtcDays(date, delta);
  }

  _buildBucketDescriptors(granularity = 'day') {
    const rangeStart = parseRecordDate(this.range?.start);
    const rangeEndExclusive = parseRecordDate(this.range?.endExclusive);

    if (!rangeStart || !rangeEndExclusive) {
      return [];
    }

    const descriptors = [];

    if (granularity === 'month') {
      let cursor = new Date(Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), 1));
      while (cursor < rangeEndExclusive) {
        descriptors.push({
          key: formatMonthKey(cursor),
          date: formatMonthKey(cursor),
          label: formatBucketLabel(cursor, 'month'),
          bucketStart: new Date(cursor),
        });
        cursor = addUtcMonths(cursor, 1);
      }
      return descriptors;
    }

    if (granularity === 'week') {
      let cursor = startOfUtcWeek(rangeStart);
      while (cursor < rangeEndExclusive) {
        descriptors.push({
          key: formatDayKey(cursor),
          date: formatDayKey(cursor),
          label: formatBucketLabel(cursor, 'week'),
          bucketStart: new Date(cursor),
        });
        cursor = addUtcDays(cursor, 7);
      }
      return descriptors;
    }

    if (granularity === 'hour' || granularity === 'minute') {
      // Avoid generating full-range descriptors for minute/hour granularity (could explode).
      // We instead generate buckets based on loaded records in getVisualizationData().
      return [];
    }

    let cursor = startOfUtcDay(rangeStart);
    while (cursor < rangeEndExclusive) {
      descriptors.push({
        key: formatDayKey(cursor),
        date: formatDayKey(cursor),
        label: formatBucketLabel(cursor, 'day'),
        bucketStart: new Date(cursor),
      });
      cursor = addUtcDays(cursor, 1);
    }
    return descriptors;
  }

  _getChartPointLimit(chartType = 'plot') {
    return INFERENCE_CHART_POINT_LIMITS[chartType] || 2000;
  }

  _normalizeGranularity(granularity = 'day') {
    return INFERENCE_VISUALIZATION_GRANULARITIES.includes(granularity)
      ? granularity
      : 'day';
  }

  _getBucketUnitMs(granularity = 'day') {
    if (granularity === 'minute') return 60 * 1000;
    if (granularity === 'hour') return 60 * 60 * 1000;
    if (granularity === 'day') return 24 * 60 * 60 * 1000;
    if (granularity === 'week') return 7 * 24 * 60 * 60 * 1000;
    return null;
  }

  _getAlignedVisualizationDomain(rangeStartDate, rangeEndExclusiveDate, granularity = 'day') {
    if (!rangeStartDate || !rangeEndExclusiveDate) {
      return null;
    }

    const normalizedGranularity = this._normalizeGranularity(granularity);
    const startBucket = this._getBucketStart(rangeStartDate, normalizedGranularity);
    const lastIncluded = new Date(rangeEndExclusiveDate.getTime() - 1);
    const lastIncludedBucketStart = this._getBucketStart(lastIncluded, normalizedGranularity);
    const endBucketExclusive = this._shiftBucketStart(
      lastIncludedBucketStart,
      normalizedGranularity,
      1
    );

    if (!startBucket || !endBucketExclusive) {
      return null;
    }

    return {
      startBucket,
      endBucketExclusive,
      axisStartLabel: formatBucketLabel(startBucket, normalizedGranularity),
      axisEndLabel: formatBucketLabel(lastIncludedBucketStart, normalizedGranularity),
    };
  }

  _getTheoreticalBucketCount(rangeStartDate, rangeEndExclusiveDate, granularity = 'day') {
    const domain = this._getAlignedVisualizationDomain(
      rangeStartDate,
      rangeEndExclusiveDate,
      granularity
    );
    if (!domain) {
      return 0;
    }

    if (granularity === 'month') {
      return Math.max(
        0,
        (domain.endBucketExclusive.getUTCFullYear() - domain.startBucket.getUTCFullYear()) * 12 +
          (domain.endBucketExclusive.getUTCMonth() - domain.startBucket.getUTCMonth())
      );
    }

    const unitMs = this._getBucketUnitMs(granularity);
    if (!unitMs) {
      return 0;
    }

    return Math.max(
      0,
      Math.round((domain.endBucketExclusive.getTime() - domain.startBucket.getTime()) / unitMs)
    );
  }

  _resolveVisualizationGranularity({
    requestedGranularity = 'day',
    rangeStartDate = null,
    rangeEndExclusiveDate = null,
    chartType = 'plot',
  } = {}) {
    const normalizedRequested = this._normalizeGranularity(requestedGranularity);
    const startIndex = INFERENCE_VISUALIZATION_GRANULARITIES.indexOf(normalizedRequested);
    const pointLimit = this._getChartPointLimit(chartType);

    let finalResult = null;

    for (
      let index = startIndex;
      index < INFERENCE_VISUALIZATION_GRANULARITIES.length;
      index += 1
    ) {
      const candidateGranularity = INFERENCE_VISUALIZATION_GRANULARITIES[index];
      const theoreticalBucketCount = this._getTheoreticalBucketCount(
        rangeStartDate,
        rangeEndExclusiveDate,
        candidateGranularity
      );
      const domain = this._getAlignedVisualizationDomain(
        rangeStartDate,
        rangeEndExclusiveDate,
        candidateGranularity
      );

      finalResult = {
        requestedGranularity: normalizedRequested,
        effectiveGranularity: candidateGranularity,
        theoreticalBucketCount,
        pointLimit,
        domain,
        canRender: theoreticalBucketCount <= pointLimit,
      };

      if (theoreticalBucketCount <= pointLimit) {
        return {
          ...finalResult,
          wasEscalated: candidateGranularity !== normalizedRequested,
          message:
            candidateGranularity !== normalizedRequested
              ? `${normalizedRequested} granularity is too dense for this range. Showing ${candidateGranularity} instead.`
              : null,
        };
      }
    }

    return {
      ...(finalResult || {
        requestedGranularity: normalizedRequested,
        effectiveGranularity: null,
        theoreticalBucketCount: 0,
        pointLimit,
        domain: null,
        canRender: false,
      }),
      wasEscalated: false,
      effectiveGranularity: null,
      message: `This range would require ${finalResult?.theoreticalBucketCount?.toLocaleString?.() || 'too many'} buckets. Even month granularity exceeds the ${pointLimit.toLocaleString()} point limit.`,
      canRender: false,
    };
  }

  getVisualizationData(options = {}) {
    const {
      granularity = 'day',
      metric = 'records',
      breakdownField = 'none',
      breakdownScope = 'all',
      breakdownValue = '',
      chartType = 'plot',
    } = options;

    const rangeStartDate = parseRecordDate(this.range?.start);
    const rangeEndExclusiveDate = parseRecordDate(this.range?.endExclusive);
    const resolution = this._resolveVisualizationGranularity({
      requestedGranularity: granularity,
      rangeStartDate,
      rangeEndExclusiveDate,
      chartType,
    });

    if (!resolution.canRender || !resolution.domain) {
      return {
        chartData: [],
        seriesKeys: [],
        metricLabel: this.getMetricLabel(metric),
        breakdownField,
        xDomainStartMs: null,
        xDomainEndExclusiveMs: null,
        axisStartLabel: null,
        axisEndLabel: null,
        requestedGranularity: resolution.requestedGranularity,
        effectiveGranularity: resolution.effectiveGranularity,
        wasEscalated: resolution.wasEscalated,
        theoreticalBucketCount: resolution.theoreticalBucketCount,
        pointLimit: resolution.pointLimit,
        canRender: false,
        message: resolution.message,
      };
    }

    const effectiveGranularity = resolution.effectiveGranularity;
    const domainStartMs = resolution.domain.startBucket.getTime();
    const domainEndExclusiveMs = resolution.domain.endBucketExclusive.getTime();

    const allBreakdownValues = this.getBreakdownValues(breakdownField);
    const activeSeriesKeys =
      breakdownField === 'none'
        ? []
        : breakdownScope === 'single' && breakdownValue
          ? allBreakdownValues.filter((value) => value === breakdownValue)
          : allBreakdownValues;

    const bucketMap = new Map();

    const ensureBucket = (bucketStart, overrides = {}) => {
      if (!(bucketStart instanceof Date) || Number.isNaN(bucketStart.getTime())) {
        return null;
      }

      const key = overrides.key || this._getBucketKey(bucketStart, effectiveGranularity);
      if (!key) {
        return null;
      }

      const existing = bucketMap.get(key);
      if (existing) {
        if (overrides.isRealData) existing.isRealData = true;
        if (overrides.isSynthetic) existing.isSynthetic = true;
        if (overrides.label) existing.label = overrides.label;
        return existing;
      }

      const bucket = {
        key,
        date: overrides.date || key,
        label: overrides.label || formatBucketLabel(bucketStart, effectiveGranularity),
        bucketStart,
        total: 0,
        series: {},
        isRealData: Boolean(overrides.isRealData),
        isSynthetic: Boolean(overrides.isSynthetic),
      };
      bucketMap.set(key, bucket);
      return bucket;
    };

    // One bucket per time slice across the resolved range (zeros where there are no records).
    const domainStartBucket = resolution.domain.startBucket;
    const domainEndExclusiveBucket = resolution.domain.endBucketExclusive;
    let rangeCursor = new Date(domainStartBucket.getTime());
    const rangeEndMs = domainEndExclusiveBucket.getTime();
    while (rangeCursor.getTime() < rangeEndMs) {
      ensureBucket(new Date(rangeCursor.getTime()), { isSynthetic: true });
      const nextCursor = this._shiftBucketStart(rangeCursor, effectiveGranularity, 1);
      if (
        !nextCursor ||
        Number.isNaN(nextCursor.getTime()) ||
        nextCursor.getTime() <= rangeCursor.getTime()
      ) {
        break;
      }
      rangeCursor = nextCursor;
    }

    for (const record of this.records || []) {
      const startedAt = parseRecordDate(record?.startedAt);
      if (!startedAt) {
        continue;
      }

      const metricValue = this._getMetricValue(record, metric);
      if (!isFiniteNumber(metricValue) || metricValue === 0) {
        continue;
      }

      const bucketStart = this._getBucketStart(startedAt, effectiveGranularity);
      const bucket = ensureBucket(bucketStart, { isRealData: true });
      if (!bucket) {
        continue;
      }

      if (breakdownField === 'none') {
        bucket.total += metricValue;
        continue;
      }

      const seriesKey = this._getBreakdownValue(record, breakdownField);
      if (!seriesKey) {
        continue;
      }

      if (breakdownScope === 'single' && breakdownValue && seriesKey !== breakdownValue) {
        continue;
      }

      bucket.total += metricValue;
      bucket.series[seriesKey] = (bucket.series[seriesKey] || 0) + metricValue;
    }

    const chartData = Array.from(bucketMap.values()).sort(
      (a, b) => a.bucketStart.getTime() - b.bucketStart.getTime()
    );

    return {
      chartData,
      seriesKeys: activeSeriesKeys,
      metricLabel: this.getMetricLabel(metric),
      breakdownField,
      xDomainStartMs: domainStartMs,
      xDomainEndExclusiveMs: domainEndExclusiveMs,
      axisStartLabel: resolution.domain.axisStartLabel,
      axisEndLabel: resolution.domain.axisEndLabel,
      requestedGranularity: resolution.requestedGranularity,
      effectiveGranularity,
      wasEscalated: resolution.wasEscalated,
      theoreticalBucketCount: resolution.theoreticalBucketCount,
      pointLimit: resolution.pointLimit,
      canRender: true,
      message: resolution.message,
    };
  }
}
